import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, Edit } from 'lucide-react';
import { formatNumber } from '../../utils/constants';
import { CAPITAL_CATEGORIES, API_URL } from './helpers';

export const CapitalTab = ({ capital, totalCapital, retainedEarnings, totalEquity, isAdmin, canDeleteCapital, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newCapital, setNewCapital] = useState({ category: '', name: '', description: '', amount: '', date: new Date().toISOString().split('T')[0] });
  const [editingCapital, setEditingCapital] = useState(null);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      let amount = parseFloat(newCapital.amount);
      if (newCapital.category === 'Owner Withdrawal' || newCapital.category === 'Owner Distribution') {
        amount = -Math.abs(amount);
      }
      await axios.post(`${API_URL}/capital`, {
        ...newCapital,
        amount: amount,
        date: new Date(newCapital.date).toISOString()
      });
      toast.success('Capital entry created');
      setDialogOpen(false);
      setNewCapital({ category: '', name: '', description: '', amount: '', date: new Date().toISOString().split('T')[0] });
      onRefresh();
    } catch (error) {
      toast.error('Failed to create capital entry');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingCapital) return;
    try {
      let amount = parseFloat(editingCapital.amount);
      if (editingCapital.category === 'Owner Withdrawal' || editingCapital.category === 'Owner Distribution') {
        amount = -Math.abs(amount);
      }
      await axios.put(`${API_URL}/capital/${editingCapital.id}`, {
        ...editingCapital,
        amount: amount,
        date: new Date(editingCapital.date).toISOString()
      });
      toast.success('Capital entry updated');
      setEditDialogOpen(false);
      setEditingCapital(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update capital entry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this capital entry?')) return;
    try {
      await axios.delete(`${API_URL}/capital/${id}`);
      toast.success('Capital entry deleted');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete capital entry');
    }
  };

  const openEdit = (entry) => {
    setEditingCapital({
      ...entry,
      amount: Math.abs(entry.amount),
      date: entry.date ? entry.date.split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setEditDialogOpen(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Owner's Capital</h3>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-capital-btn"><Plus className="w-4 h-4 mr-2" />Add Capital Entry</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Capital Entry</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Type *</label>
                <Select value={newCapital.category} onValueChange={(v) => setNewCapital({...newCapital, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {CAPITAL_CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Name/Reference *</label>
                <Input value={newCapital.name} onChange={(e) => setNewCapital({...newCapital, name: e.target.value})} placeholder="e.g., Initial Investment, Monthly Withdrawal" required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea value={newCapital.description} onChange={(e) => setNewCapital({...newCapital, description: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Amount (RWF) *</label>
                <Input type="number" step="0.01" value={newCapital.amount} onChange={(e) => setNewCapital({...newCapital, amount: e.target.value})} required />
                <p className="text-xs text-muted-foreground mt-1">For withdrawals, enter as positive - system will record as negative</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Date *</label>
                <Input type="date" value={newCapital.date} onChange={(e) => setNewCapital({...newCapital, date: e.target.value})} required />
              </div>
              <Button type="submit" className="w-full">Create Entry</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Equity Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
          <span className="text-sm font-medium text-purple-800">Total Capital</span>
          <p className="text-lg font-bold text-purple-700">RWF {formatNumber(totalCapital)}</p>
        </div>
        <div className={`p-4 rounded-lg border ${retainedEarnings >= 0 ? 'bg-indigo-50 border-indigo-100' : 'bg-orange-50 border-orange-100'}`}>
          <span className={`text-sm font-medium ${retainedEarnings >= 0 ? 'text-indigo-800' : 'text-orange-800'}`}>Retained Earnings</span>
          <p className={`text-lg font-bold ${retainedEarnings >= 0 ? 'text-indigo-700' : 'text-orange-700'}`}>RWF {formatNumber(retainedEarnings)}</p>
        </div>
        <div className={`p-4 rounded-lg border ${totalEquity >= 0 ? 'bg-blue-50 border-blue-100' : 'bg-red-50 border-red-100'}`}>
          <span className={`text-sm font-medium ${totalEquity >= 0 ? 'text-blue-800' : 'text-red-800'}`}>Total Equity</span>
          <p className={`text-lg font-bold ${totalEquity >= 0 ? 'text-blue-700' : 'text-red-700'}`}>RWF {formatNumber(totalEquity)}</p>
        </div>
      </div>

      {/* Capital Table */}
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="capital-table">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Type</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Name</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Description</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Amount</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Date</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {capital.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No capital entries recorded yet.</td></tr>
            )}
            {capital.map((entry) => (
              <tr key={entry.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 text-sm">{entry.category}</td>
                <td className="px-4 py-3 text-sm font-medium text-foreground">{entry.name}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{entry.description || '-'}</td>
                <td className={`px-4 py-3 text-sm font-semibold ${entry.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  RWF {formatNumber(entry.amount)}
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {entry.date ? new Date(entry.date).toLocaleDateString() : '-'}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {isAdmin && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(entry)}><Edit className="w-4 h-4" /></Button>
                        {canDeleteCapital && <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Capital Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Capital Entry</DialogTitle></DialogHeader>
          {editingCapital && (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Type *</label>
                <Select value={editingCapital.category} onValueChange={(v) => setEditingCapital({...editingCapital, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CAPITAL_CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <Input value={editingCapital.name} onChange={(e) => setEditingCapital({...editingCapital, name: e.target.value})} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea value={editingCapital.description || ''} onChange={(e) => setEditingCapital({...editingCapital, description: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Amount (RWF) *</label>
                <Input type="number" step="0.01" value={editingCapital.amount} onChange={(e) => setEditingCapital({...editingCapital, amount: e.target.value})} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Date *</label>
                <Input type="date" value={editingCapital.date} onChange={(e) => setEditingCapital({...editingCapital, date: e.target.value})} required />
              </div>
              <Button type="submit" className="w-full">Update Entry</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
